import {MouseEvent} from 'react'
import cn from 'clsx'
import {parseISO, differenceInCalendarDays} from 'date-fns'
import {Avatar, Space} from 'antd'

import styles from './Notification.module.scss'
import {REQUEST_STATUS_NAME, RequestStatus} from 'modules/request'
import {DrawdownTag, isProjectRequest} from 'modules/project'
import {getShortName} from 'modules/users'
import {useReadNotification} from 'common/api'
import {
  Text,
  Block,
  Box,
  Tag,
  UserAvatar,
  AttachmentLinkIcon,
  CheckButton,
} from 'common/components'
import {formatDate, cutText, formatMoney, maybePluralize} from 'common/helpers'
import {NotificationType} from 'common/types'

interface NotificationProps {
  isCategoryReading?: boolean
  notification: any
}

interface ContentTextProps {
  text: string
}

interface ContentProps {
  type: NotificationType
  data: any
}

export default function Notification({
  isCategoryReading,
  notification,
}: NotificationProps) {
  const {mutate: readNotification, isMutating} = useReadNotification(
    notification.uuid
  )
  const isChecked = isMutating || isCategoryReading || notification.read

  const handleCheck = isChecked
    ? undefined
    : (e: MouseEvent) => {
        e.preventDefault()
        e.stopPropagation()
        readNotification()
      }

  const {project} = notification.data
  const {manager, borrower} = project
  const isRequest = isProjectRequest(project)
  const isDrawdown = notification.type === 'drawdownStatusUpdated'
  const isVariation= (notification.type.indexOf('variation') > -1)
  const isRequestOrDrawdown = isRequest || isDrawdown
  const date = parseISO(notification.createdAt)

  // console.log(notification.type, isVariation)

  const to = isChecked
    ? undefined
    : notification.type === 'loanRequestStatusUpdated' ||
      notification.type === 'loanRequestPushedBack' ||
      notification.type === 'amlCheckReadyForReview'
    ? {
        pathname: `/requests/loan/${notification.data.loanRequestUuid}`,
      }
    : notification.type === 'newChatMessage'
    ? {
        pathname: `/projects/${project.uuid}`,
        hash: 'chat',
      }
    : notification.type === 'drawdownStatusUpdated'
    ? {
        pathname: `/projects/${project.uuid}/drawdowns`,
        hash: `${notification.data.drawdownUuid}`,
      }
    : notification.type === 'transactionCreated'
    ? `/projects/${project.uuid}/schedule`
    : notification.type.startsWith('forecast')
    ? `/projects/${project.uuid}/forecast`
    : isRequest
    ? '/requests'
    : `/projects/${project.uuid}`

  return (
    <li className={styles.root}>
      <Block
        className={styles.block}
        type={isChecked ? 'grey' : undefined}
        to={to}
      >
        <div className={styles.project}>
          <Avatar
            className={styles.image}
            src={project.image}
            shape="square"
            size={72}
            alt={project.title}
          />
          <div className={styles.details}>
            <div>
              <p
                className={cn(
                  styles.title,
                  isRequestOrDrawdown && styles.title_cut
                )}
              >
                {isRequestOrDrawdown ? project.title : cutText(project.title)}
              </p>
              {(isRequestOrDrawdown || isVariation) && (
                <Text color="grey" size="xs">
                  {
                    isVariation ? 'Variation' :
                    isDrawdown ? 'Drawdown' : 'Loan'
                  } request
                </Text>
              )}
            </div>
            <Space className={styles.users} size={6}>
              <Avatar.Group>
                <UserAvatar
                  size={18}
                  src={borrower?.photo}
                  alt={borrower?.firstName}
                />
                {manager && (
                  <UserAvatar
                    size={18}
                    src={manager.photo}
                    alt={manager.firstName}
                  />
                )}
              </Avatar.Group>
              <span>
                {/* <span>{getShortName(borrower)}</span> */}
                {manager && (
                  <>
                    <Box as="span" color="grey" ml={5} mr={5}>
                      <AttachmentLinkIcon />
                    </Box>
                    <span>{getShortName(manager)}</span>
                  </>
                )}
              </span>
            </Space>
          </div>
        </div>
        <div className={styles.content}>
          <Content type={notification.type} data={notification.data} />
        </div>
        <div className={styles.lastBlock}>
          <CheckButton
            className={styles.check}
            checked={isChecked}
            onClick={handleCheck}
          />
          <Text size="xs" color="grey">
            {formatDate(date)}
            <br />
            at {formatDate(date, 'tenth')}
          </Text>
        </div>
      </Block>
    </li>
  )
}

const ContentText = ({text}: ContentTextProps) => (
  <Text size="xs">{cutText(text, 80)}</Text>
)

const Content = ({type, data}: ContentProps) => {
  if (type === 'newChatMessage') {
    const text = data.message.attachment
      ? data.message.attachment.name
      : data.message.message
    return (
      <>
        <Tag icon="message">
          new {data.message.attachment ? 'attachment' : 'message'}
        </Tag>
        <ContentText text={text} />
      </>
    )
  }

  if (type === 'loanRequestStatusUpdated') {
    const isApproved = data.status === 'approved'
    const isDeclined = data.status === 'declined'
    return (
      <>
        <Tag
          icon={isApproved ? 'request' : isDeclined ? 'close' : 'check'}
          color={isApproved ? 'success' : isDeclined ? 'danger' : undefined}
        >
          {REQUEST_STATUS_NAME[data.status as RequestStatus]}
        </Tag>
        <ContentText
          text={`${data.loanValue ? 'Loan' : 'Requested loan'} ${formatMoney(
            data.loanValue || data.loanValueRequested
          )} nzd`}
        />
      </>
    )
  }

  if (type === 'drawdownStatusUpdated') {
    return (
      <>
        <DrawdownTag status={data.status} />
        <ContentText
          text={`Drawdown "${data.drawdownTitle}", ${formatMoney(
            data.requestedAmount
          )} nzd requested, ${formatMoney(data.approvedAmount)} nzd approved`}
        />
      </>
    )
  }

  if (type === 'projectFeedbackAdded')
    return (
      <>
        <Tag icon="message">Feedback added</Tag>
        <ContentText text={data.text} />
      </>
    )

  if (type === 'forecastDue')
    return (
      <>
        <Tag color="danger" icon="blackboard">
          Forecast due
        </Tag>
        <ContentText
          text={`Forecast has been updated ${formatDate(
            data.forecastUpdatedAt
          )}`}
        />
      </>
    )

  if (type === 'forecastOverdue')
    return (
      <>
        <Tag color="danger" icon="blackboard">
          Forecast overdue
        </Tag>
        <ContentText
          text={`Forecast for ${formatDate(
            data.forecastUpdatedAt
          )} is ready to be estimated`}
        />
      </>
    )

  if (type === 'forecastUpdated')
    return (
      <>
        <Tag icon="blackboard">Forecast updated</Tag>
        <ContentText
          text={`Forecast has been updated ${formatDate(
            data.forecastUpdatedAt
          )}`}
        />
      </>
    )

  if (type === 'projectConditionDue')
    return (
      <>
        <Tag color="danger" icon="shield">
          Condition due
        </Tag>
        <ContentText text={`Condition for ${formatDate(data.date)}`} />
      </>
    )

  if (type === 'projectConditionOverdue')
    return (
      <>
        <Tag color="danger" icon="shield">
          Condition overdue
        </Tag>
        <ContentText
          text={`Condition ${maybePluralize(
            differenceInCalendarDays(new Date(), parseISO(data.date)),
            'day'
          )} overdue`}
        />
      </>
    )

  if (type === 'projectMaturityDateOverdue')
    return (
      <>
        <Tag color="danger" icon="request">
          Maturity date overdue
        </Tag>
        <ContentText
          text={`Maturity expired ${maybePluralize(
            differenceInCalendarDays(new Date(), parseISO(data.maturityDate)),
            'day'
          )} ago. Maturity date is ${formatDate(data.maturityDate)}`}
        />
      </>
    )

  if (type === 'transactionCreated')
    return (
      <>
        <Tag color="success" icon="check">
          {data.transactionType}
        </Tag>
        <ContentText
          text={`Transaction ${data.transactionType} with amount ${formatMoney(
            data.transactionAmount
          )} has been created`}
        />
      </>
    )

  if (type === 'loanRequestPushedBack')
    return (
      <>
        <Tag color="danger" icon="close">
          Request pushed back
        </Tag>
        <ContentText text="Request has been pushed back" />
      </>
    )

  if (type === 'amlCheckReadyForReview') {
    return (
      <>
        <Tag color="success" icon="check">
          AML check ready
        </Tag>
        <ContentText text="AML check is ready for review" />
      </>
    )
  }

  if (!!type && (type as string).indexOf('variationRequest') > -1) {
    return (
      <>
        <Tag color="success" icon="check">
          AML check ready
        </Tag>
        <ContentText text="AML check is ready for review" />
      </>
    )
  }

  return null
}
